/**
 * StudioHtmlImportDialog.tsx — Importar HTML pronto para dentro do modelo
 *
 * Passos da janela:
 *   1. o usuário cola o HTML (ou escolhe um arquivo .html);
 *   2. o conteúdo é enviado ao servidor, que faz a limpeza que vale, guarda as
 *      imagens embutidas como arquivos e devolve o relatório;
 *   3. o resultado aparece em pré-visualização isolada (iframe sem permissões);
 *   4. só depois o usuário confirma e o conteúdo entra no modelo.
 *
 * RELEASE: v1.1.0 - 2026-09-11 - O conteúdo entra FATIADO em seções editáveis:
 *          cada faixa da página vira um bloco comum, que pode ser movido,
 *          duplicado, apagado e ter os textos trocados sem mexer em código.
 * RELEASE: v1.0.0 - 2026-09-10 - Versão inicial (Studio, Fase 4)
 */

import React, { useRef, useState } from 'react';
import { FileUp, Loader2, ShieldCheck } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { InlineNotice } from '@/components/ui/inline-notice';
import { ScrollArea } from '@/components/ui/scroll-area';

import { importStudioHtml, type StudioImportResult } from '@/lib/studio/import';
import type { StudioHtmlSectionData } from '@/lib/studio/html/section';
import { toast } from '@/lib/toast';
import { logger } from '@/lib/logger';

interface StudioHtmlImportDialogProps {
  open: boolean;
  onOpenChange: (aberto: boolean) => void;
  /** Modelo em edição — usado para guardar as imagens junto dele. */
  templateId?: string | null;
  /**
   * Confirmação: devolve as seções já limpas e fatiadas pelo servidor.
   * O HTML inteiro segue junto, para o modo código de quem é administrador.
   */
  onConfirm: (secoes: StudioHtmlSectionData[], html: string) => void;
}

export function StudioHtmlImportDialog({
  open,
  onOpenChange,
  templateId,
  onConfirm,
}: StudioHtmlImportDialogProps) {
  const [texto, setTexto] = useState('');
  const [conferindo, setConferindo] = useState(false);
  const [resultado, setResultado] = useState<StudioImportResult | null>(null);
  const inputArquivo = useRef<HTMLInputElement | null>(null);

  function limpar() {
    setTexto('');
    setResultado(null);
  }

  async function aoEscolherArquivo(evento: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = evento.target.files?.[0];
    if (!arquivo) return;
    const conteudo = await arquivo.text();
    setTexto(conteudo);
    setResultado(null);
    evento.target.value = '';
  }

  async function aoConferir() {
    if (!texto.trim()) {
      toast.error('Cole o conteúdo ou escolha um arquivo.');
      return;
    }
    setConferindo(true);
    try {
      const retorno = await importStudioHtml(texto, templateId ?? null);
      setResultado(retorno);
    } catch (erro) {
      logger.error('studio', 'Falha ao conferir HTML importado', erro);
      toast.error('Não foi possível processar o conteúdo enviado.');
    } finally {
      setConferindo(false);
    }
  }

  function aoConfirmar() {
    if (!resultado) return;
    onConfirm(resultado.sections ?? [], resultado.html);
    onOpenChange(false);
    limpar();
  }

  const relatorio = resultado?.report;
  const removidos = relatorio
    ? Object.entries(relatorio.removedElements).concat(Object.entries(relatorio.removedAttributes))
    : [];

  return (
    <Dialog
      open={open}
      onOpenChange={(valor) => {
        onOpenChange(valor);
        if (!valor) limpar();
      }}
    >
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Importar HTML</DialogTitle>
          <DialogDescription>
            O conteúdo é limpo pelo servidor antes de entrar no modelo. Programação embutida é
            sempre removida.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 md:grid-cols-2">
          {/* ------------------------------------------------ Entrada */}
          <div className="flex min-h-[320px] flex-col gap-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 gap-1.5"
                onClick={() => inputArquivo.current?.click()}
              >
                <FileUp className="h-3.5 w-3.5" />
                Escolher arquivo
              </Button>
              <input
                ref={inputArquivo}
                type="file"
                accept=".html,.htm,text/html"
                className="hidden"
                onChange={aoEscolherArquivo}
              />
              <Button
                type="button"
                size="sm"
                className="h-8 gap-1.5"
                onClick={aoConferir}
                disabled={conferindo}
              >
                {conferindo ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <ShieldCheck className="h-3.5 w-3.5" />
                )}
                Conferir
              </Button>
            </div>

            <Textarea
              value={texto}
              onChange={(e) => {
                setTexto(e.target.value);
                setResultado(null);
              }}
              placeholder="Cole aqui o HTML do documento"
              className="min-h-[260px] flex-1 font-mono text-xs"
            />
          </div>

          {/* ------------------------------------------------ Conferência */}
          <div className="flex min-h-[320px] flex-col gap-2">
            {!resultado && (
              <InlineNotice variant="info">
                Depois de conferir, o resultado aparece aqui em uma janela isolada, do jeito que vai
                ficar no documento.
              </InlineNotice>
            )}

            {resultado && (
              <ScrollArea className="h-[320px] pr-2">
                <div className="space-y-3">
                  <div className="rounded-lg border bg-white">
                    <iframe
                      title="Conferência do conteúdo importado"
                      sandbox=""
                      srcDoc={resultado.html}
                      className="h-[200px] w-full rounded-lg border-0"
                    />
                  </div>

                  {resultado.sections_report && (
                    <InlineNotice variant="success">
                      A página foi dividida em {resultado.sections_report.sections} seção(ões) e{' '}
                      {resultado.sections_report.fields} conteúdo(s) ficaram editáveis sem precisar
                      mexer no código.
                    </InlineNotice>
                  )}

                  {relatorio && (
                    <div className="space-y-2 text-xs">
                      <div>
                        <p className="mb-1 font-medium">Variáveis encontradas</p>
                        {relatorio.variables.length ? (
                          <div className="flex flex-wrap gap-1">
                            {relatorio.variables.map((v) => (
                              <Badge key={v} variant="secondary" className="h-5 text-[10px]">
                                {v}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">Nenhuma.</p>
                        )}
                      </div>

                      <div>
                        <p className="mb-1 font-medium">Encaixes do sistema</p>
                        {relatorio.slots.length ? (
                          <div className="flex flex-wrap gap-1">
                            {relatorio.slots.map((s) => (
                              <Badge key={s} variant="outline" className="h-5 text-[10px]">
                                {s}
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">Nenhum.</p>
                        )}
                      </div>

                      <div>
                        <p className="mb-1 font-medium">Imagens</p>
                        <p className="text-muted-foreground">
                          {relatorio.savedImages.length} guardada(s) na conta ·{' '}
                          {relatorio.externalImages.length} vinda(s) de outros endereços
                        </p>
                      </div>

                      <div>
                        <p className="mb-1 font-medium">Itens removidos por segurança</p>
                        {removidos.length ? (
                          <div className="flex flex-wrap gap-1">
                            {removidos.map(([nome, qtd]) => (
                              <Badge key={nome} variant="destructive" className="h-5 text-[10px]">
                                {nome} ({qtd})
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <p className="text-muted-foreground">Nada precisou ser removido.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </ScrollArea>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button type="button" onClick={aoConfirmar} disabled={!resultado}>
            Usar este conteúdo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
